import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Input,
  Card,
  Tag,
  Space,
  Popconfirm,
  Modal,
  Form,
  message,
  Select,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../api";

const { TextArea } = Input;
const { Option } = Select;

const EmailSubscriptionIndex = () => {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const page = searchParams.get("page") || 1;
      const perPage = searchParams.get("per_page") || 10;
      const search = searchParams.get("search") || "";
      const date = searchParams.get("date") || "";
      const status = searchParams.get("status") || "";

      const response = await api.get("/panel/ticket", {
        params: {
          page,
          per_page: perPage,
          search,
          date,
          status,
          type: "email_subscription",
        },
      });

      setRequests(response.data.data);
      setPagination({
        current: response.data.meta.current_page,
        pageSize: parseInt(perPage),
        total: response.data.meta.total,
      });
    } catch (error) {
      message.error("دریافت درخواست‌ها با خطا مواجه شد");
      console.error("خطا در دریافت درخواست‌ها:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/panel/ticket/${id}`);
      message.success("درخواست با موفقیت حذف شد");
      fetchRequests();
    } catch (error) {
      message.error("خطا در حذف درخواست");
    }
  };

  const handleReply = (record) => {
    setSelectedRequest(record);
    setIsModalVisible(true);
    form.setFieldsValue({
      body: record.body,
      answer: record.answer,
    });
  };

  const handleSubmitReply = async (values) => {
    try {
      await api.put(`/panel/ticket/${selectedRequest.id}`, values);
      message.success("پاسخ با موفقیت ارسال شد");
      setIsModalVisible(false);
      fetchRequests();
    } catch (error) {
      message.error("خطا در ارسال پاسخ");
    }
  };

  // تغییر صفحه
  const handleTableChange = (pagination) => {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      page: pagination.current,
      per_page: pagination.pageSize,
    });
  };

  useEffect(() => {
    fetchRequests();
  }, [searchParams]);

  const columns = [
    {
      title: "شناسه",
      dataIndex: "id",
      key: "id",
    },
    
    {
      title: "ایمیل",
      dataIndex: "email",
      key: "email",
    },
    
    {
      title: "وضعیت",
      dataIndex: "answer",
      key: "answer",
      render: (answer) => (
        <Tag color={answer ? "green" : "red"}>
          {answer ? "پاسخ داده شده" : "پاسخ داده نشده"}
        </Tag>
      ),
    },
    {
      title: "تاریخ ایجاد",
      dataIndex: "created_at",
      key: "created_at",
    },
    {
      title: "عملیات",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleReply(record)}
          >
            پاسخ
          </Button>

          <Popconfirm
            title="آیا از حذف این درخواست اطمینان دارید؟"
            onConfirm={() => handleDelete(record.id)}
            okText="بله"
            cancelText="خیر"
          >
            <Button type="primary" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold"> خبرنامه</h1>
        </div>

        <div className="mb-4 flex gap-4">
          <Input
            placeholder="جستجو..."
            prefix={<SearchOutlined />}
            allowClear
            onChange={(e) =>
              setSearchParams({
                ...Object.fromEntries(searchParams.entries()),
                search: e.target.value,
                page: 1,
              })
            }
            style={{ width: 400 }}
          />

          <Select
            placeholder="وضعیت"
            onChange={(value) =>
              setSearchParams({
                ...Object.fromEntries(searchParams.entries()),
                status: value,
                page: 1,
              })
            }
            style={{ width: 150 }}
            allowClear
          >
            <Option value="answered">پاسخ داده شده</Option>
            <Option value="unanswered">پاسخ داده نشده</Option>
          </Select>
        </div>

        <Table
          columns={columns}
          dataSource={requests}
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `مجموع ${total} درخواست`,
          }}
          onChange={handleTableChange}
          scroll={{ x: true }}
        />

        <Modal
          title="پاسخ به درخواست"
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={null}
        >
          <Form form={form} layout="vertical" onFinish={handleSubmitReply}>
            <Form.Item name="body" label="متن درخواست">
              <TextArea rows={4}  />
            </Form.Item>

            <Form.Item
              name="answer"
              label="پاسخ"
              rules={[{ required: true, message: "پاسخ الزامی است" }]}
            >
              <TextArea rows={4} placeholder="پاسخ خود را وارد کنید" />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit">
                ارسال پاسخ
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </Card>
  );
};

export default EmailSubscriptionIndex;
